import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default branding values
const DEFAULT_BRANDING = {
  portal_title: "Aniyor Admin",
  logo_url: null,
  favicon_url: null,
  colors: {
    primary: "#3B82F6",
    secondary: "#1E293B",
    accent: "#F59E0B",
    text_primary: "#111827"
  },
  footer: {
    show_footer: true,
    copyright_text: "© 2026 Aniyor Marketplace. All rights reserved.",
    disclaimer_text: "Authorized personnel only."
  }
};

// Try to get cached branding from localStorage for instant display
const getCachedBranding = () => {
  try {
    const cached = localStorage.getItem('app_branding');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return DEFAULT_BRANDING;
};

const ThemeContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  refreshBranding: () => {},
  updateBranding: () => {}
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  // Initialize with cached branding for instant display (no flash)
  const [branding, setBranding] = useState(getCachedBranding);
  const [loading, setLoading] = useState(true);

  // Apply CSS variables to :root
  const applyCSSVariables = (colors) => {
    const root = document.documentElement;
    
    // Primary brand colors
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--secondary-color', colors.secondary);
    root.style.setProperty('--accent-color', colors.accent);
    root.style.setProperty('--text-primary', colors.text_primary);
    
    // Calculate hover/focus states (slightly darker versions)
    root.style.setProperty('--primary-hover', adjustColor(colors.primary, -10));
    root.style.setProperty('--secondary-hover', adjustColor(colors.secondary, -10));
    root.style.setProperty('--accent-hover', adjustColor(colors.accent, -10));
    
    // Sidebar colors
    root.style.setProperty('--sidebar-bg', colors.secondary);
    root.style.setProperty('--sidebar-text', '#ffffff');
    root.style.setProperty('--sidebar-active-bg', colors.primary);
    
    // Button colors
    root.style.setProperty('--button-primary-bg', colors.primary);
    root.style.setProperty('--button-primary-hover', adjustColor(colors.primary, -10));
    
    // Link colors
    root.style.setProperty('--link-color', colors.primary);
    root.style.setProperty('--link-hover-color', adjustColor(colors.primary, -15));
    
  };

  // Adjust color brightness
  const adjustColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (
      0x1000000 +
      (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 0 ? 0 : B) : 255)
    ).toString(16).slice(1);
  };

  // Update document title and favicon
  const updateDocumentMeta = (brandingData) => {
    // Update title
    document.title = brandingData.portal_title || DEFAULT_BRANDING.portal_title;
    
    // Update favicon
    if (brandingData.favicon_url) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = brandingData.favicon_url;
    }
  };

  // Fetch branding from API
  const fetchBranding = async () => {
    try {
      const res = await fetch(`${API_URL}/api/branding/`);
      if (res.ok) {
        const data = await res.json();
        const newBranding = { ...DEFAULT_BRANDING, ...data };
        setBranding(newBranding);
        applyCSSVariables(data.colors || DEFAULT_BRANDING.colors);
        updateDocumentMeta(data);
        // Cache branding in localStorage for instant display on next visit
        try {
          localStorage.setItem('app_branding', JSON.stringify(newBranding));
        } catch (e) {
          // Ignore storage errors
        }
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      applyCSSVariables(DEFAULT_BRANDING.colors);
    } finally {
      setLoading(false);
    }
  };

  // Refresh branding (called after updates)
  const refreshBranding = () => {
    fetchBranding();
  };

  // Update branding locally (for live preview)
  const updateBranding = (newBranding) => {
    setBranding(newBranding);
    // Only apply CSS variables if colors exist, otherwise use defaults
    if (newBranding.colors) {
      applyCSSVariables(newBranding.colors);
    }
    updateDocumentMeta(newBranding);
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  return (
    <ThemeContext.Provider value={{ branding, loading, refreshBranding, updateBranding }}>
      <div 
        className="theme-wrapper"
        style={{
          '--primary-color': branding.colors?.primary || DEFAULT_BRANDING.colors.primary,
          '--secondary-color': branding.colors?.secondary || DEFAULT_BRANDING.colors.secondary,
          '--accent-color': branding.colors?.accent || DEFAULT_BRANDING.colors.accent,
          '--text-primary': branding.colors?.text_primary || DEFAULT_BRANDING.colors.text_primary,
        }}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
